import { useScienceTutorSection } from './scienceTutorContext';
import { CheckCircle2, LineChart, Ruler, XCircle } from 'lucide-react';
import { useState } from 'react';

import {
  CALIBRATION_CHALLENGES,
  CALIBRATION_EXPLORE_FIELDS,
  CALIBRATION_QUESTION,
  type CalibrationExploreResult,
  type CalibrationExploreSetup,
  calculateCalibrationExplore,
  defaultCalibrationSetup,
} from './instrumentalExplore';
import { ExploreChallenges } from './ExploreChallenges';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';

import {
  AAS_ELEMENT_SPECIFIC_EXPLANATION,
  MIN_CALIBRATION_STANDARDS,
  calibrationProblemsFor,
  checkCalibrationAnswer,
  fitCalibration,
  formatConcentration,
  readUnknown,
  standardReadings,
  type CalibrationFit,
  type CalibrationMode,
  type CalibrationPoint,
  type CalibrationProblem,
} from './instrumentalAnalysis';

const CHART_WIDTH = 320;
const CHART_HEIGHT = 220;
const CHART_PAD_LEFT = 44;
const CHART_PAD_BOTTOM = 32;
const CHART_PAD_TOP = 12;
const CHART_PAD_RIGHT = 12;
const AXIS_HEADROOM = 1.15;

function CalibrationChart({
  problem,
  points,
  fit,
  unknownA,
  readout = null,
}: {
  problem: CalibrationProblem;
  points: CalibrationPoint[];
  fit: CalibrationFit | null;
  unknownA: number | null;
  /** Explore: concentration read off the line for `unknownA`, drawn as a point; null outside the standards. */
  readout?: number | null;
}) {
  const allReadings = standardReadings(problem);
  const maxC = Math.max(...problem.standards) * AXIS_HEADROOM;
  const maxA =
    Math.max(...allReadings.map((point) => point.absorbance), unknownA ?? 0) * AXIS_HEADROOM;
  const plotW = CHART_WIDTH - CHART_PAD_LEFT - CHART_PAD_RIGHT;
  const plotH = CHART_HEIGHT - CHART_PAD_TOP - CHART_PAD_BOTTOM;
  const x = (c: number) => CHART_PAD_LEFT + (c / maxC) * plotW;
  const y = (a: number) => CHART_PAD_TOP + plotH - (a / maxA) * plotH;
  const baseY = CHART_PAD_TOP + plotH;

  return (
    <svg
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      className="block h-auto w-full max-w-xl"
      role="img"
      aria-label={`Calibration graph: ${points.length} standards plotted${
        fit ? `, fitted line A = ${fit.slope.toFixed(4)}c + ${fit.intercept.toFixed(4)}` : ''
      }${unknownA !== null ? `, unknown absorbance ${unknownA.toFixed(3)}` : ''}${
        readout !== null ? ` reads ${formatConcentration(readout)} ${problem.unit}` : ''
      }`}
    >
      <line x1={CHART_PAD_LEFT} x2={CHART_WIDTH - CHART_PAD_RIGHT} y1={baseY} y2={baseY} className="stroke-ink" strokeWidth={1.5} />
      <line x1={CHART_PAD_LEFT} x2={CHART_PAD_LEFT} y1={CHART_PAD_TOP} y2={baseY} className="stroke-ink" strokeWidth={1.5} />
      {problem.standards.map((c) => (
        <text key={c} x={x(c)} y={baseY + 12} textAnchor="middle" className="fill-slate2 text-[8px] font-bold">
          {formatConcentration(c)}
        </text>
      ))}
      <text x={CHART_PAD_LEFT + plotW / 2} y={CHART_HEIGHT - 4} textAnchor="middle" className="fill-ink text-[9px] font-bold">
        Concentration ({problem.unit})
      </text>
      {[0.25, 0.5, 0.75, 1].map((fraction) => (
        <text key={fraction} x={CHART_PAD_LEFT - 4} y={y((maxA / AXIS_HEADROOM) * fraction) + 3} textAnchor="end" className="fill-slate2 text-[8px] font-bold">
          {((maxA / AXIS_HEADROOM) * fraction).toFixed(2)}
        </text>
      ))}
      <text x={10} y={CHART_PAD_TOP + plotH / 2} textAnchor="middle" transform={`rotate(-90 10 ${CHART_PAD_TOP + plotH / 2})`} className="fill-ink text-[9px] font-bold">
        Absorbance
      </text>
      {fit ? (
        <line
          x1={x(fit.minConcentration)}
          x2={x(fit.maxConcentration)}
          y1={y(fit.slope * fit.minConcentration + fit.intercept)}
          y2={y(fit.slope * fit.maxConcentration + fit.intercept)}
          className="stroke-brand-sky"
          strokeWidth={2}
        />
      ) : null}
      {unknownA !== null ? (
        <g>
          <line x1={CHART_PAD_LEFT} x2={CHART_WIDTH - CHART_PAD_RIGHT} y1={y(unknownA)} y2={y(unknownA)} className="stroke-brand-coral" strokeWidth={1.5} strokeDasharray="5 4" />
          <text x={CHART_WIDTH - CHART_PAD_RIGHT} y={y(unknownA) - 4} textAnchor="end" className="fill-ink text-[8px] font-bold">
            unknown A = {unknownA.toFixed(3)}
          </text>
        </g>
      ) : null}
      {readout !== null && unknownA !== null ? (
        <g>
          <line x1={x(readout)} x2={x(readout)} y1={y(unknownA)} y2={baseY} className="stroke-brand-coral" strokeWidth={1.5} strokeDasharray="3 3" />
          <circle cx={x(readout)} cy={y(unknownA)} r={5} className="fill-brand-coral stroke-white" strokeWidth={1.5} />
        </g>
      ) : null}
      {points.map((point) => (
        <circle key={point.concentration} cx={x(point.concentration)} cy={y(point.absorbance)} r={3.5} className="fill-ink" />
      ))}
    </svg>
  );
}

/** Explore: a typed absorbance and dilution read off the line fitted to all reviewed standards. */
function CalibrationExplore({ problem }: { problem: CalibrationProblem }) {
  const [setup, setSetup] = useState<CalibrationExploreSetup>(() => defaultCalibrationSetup(problem));
  const [last, setLast] = useState<{ setup: CalibrationExploreSetup; result: CalibrationExploreResult }>(() => ({
    setup: defaultCalibrationSetup(problem),
    result: calculateCalibrationExplore(problem, defaultCalibrationSetup(problem)),
  }));
  const [error, setError] = useState<string | null>(null);
  const apply = (next: CalibrationExploreSetup) => {
    setSetup(next);
    try {
      setLast({ setup: next, result: calculateCalibrationExplore(problem, next) });
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'This setup cannot be modelled.');
    }
  };
  const { result } = last;
  return (
    <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="min-w-0 space-y-4">
        <ModelInputPanel legend="Your reading" fields={CALIBRATION_EXPLORE_FIELDS} values={setup} onChange={apply} sliderKey="absorbance" />
        {error ? (
          <p role="alert" className="rounded-2xl border border-brand-coral/40 bg-wash-coral px-3 py-2 text-sm font-bold text-ink">
            {error} The model keeps the last valid result.
          </p>
        ) : null}
        <ExploreChallenges question={CALIBRATION_QUESTION} challenges={CALIBRATION_CHALLENGES} result={{ problem, setup: last.setup, result }} />
        <WorkedStepsPanel
          title="How the model works it out"
          steps={result.steps}
          stepTestIdPrefix="calibration-step"
          result={result.result}
          resultTestId="calibration-result"
          limitNote={result.limitNote}
        />
      </div>
      <div className="min-w-0">
        <CalibrationChart
          problem={problem}
          points={standardReadings(problem)}
          fit={result.fit}
          unknownA={last.setup.absorbance}
          readout={result.measured}
        />
        <p className="mt-2 text-xs font-bold leading-5 text-slate2">
          The dashed line is your absorbance. Inside the standards the point sits on the line; outside them the model reports no value.
        </p>
      </div>
    </div>
  );
}

export function CalibrationModeView({ mode }: { mode: CalibrationMode }) {
  const problems = calibrationProblemsFor(mode);
  const [problemId, setProblemId] = useState(problems[0].id);
  const [view, setView] = useState<'explore' | 'practice'>('explore');
  const [measured, setMeasured] = useState<number[]>([]);
  const [fit, setFit] = useState<CalibrationFit | null>(null);
  const [unknownMeasured, setUnknownMeasured] = useState(false);
  const [answerText, setAnswerText] = useState('');
  useScienceTutorSection('CalibrationModeView', { problemId, measured, fit, unknownMeasured, answerText });
  const [feedback, setFeedback] = useState<{ correct: boolean; text: string } | null>(null);

  const problem = problems.find((candidate) => candidate.id === problemId) ?? problems[0];
  const readings = standardReadings(problem);
  const measuredPoints = readings.filter((point) => measured.includes(point.concentration));
  const unknownA = fit && unknownMeasured ? readUnknown(problem, fit).absorbance : null;

  const chooseProblem = (id: string) => {
    setProblemId(id);
    setMeasured([]);
    setFit(null);
    setUnknownMeasured(false);
    setAnswerText('');
    setFeedback(null);
  };

  const check = (outOfRange: boolean) => {
    if (!fit) return;
    const answer = outOfRange ? 'out-of-range' : Number(answerText);
    const result = checkCalibrationAnswer(problem, fit, answer);
    setFeedback({ correct: result.correct, text: result.feedback });
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
      <aside className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          Analysis problems
        </p>
        <div className="mt-4 grid gap-2">
          {problems.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              aria-pressed={candidate.id === problem.id}
              onClick={() => chooseProblem(candidate.id)}
              className={`rounded-2xl border px-4 py-3 text-left transition-colors ${
                candidate.id === problem.id
                  ? 'border-brand-sky bg-wash-sky text-ink'
                  : 'border-hairline bg-white text-slate2 hover:border-brand-sky/40'
              }`}
            >
              <span className="block text-sm font-extrabold">{candidate.title}</span>
              <span className="block text-xs font-medium">
                {candidate.analyte} · {candidate.wavelengthNm.toFixed(1)} nm
              </span>
            </button>
          ))}
        </div>
        <div className="mt-5 rounded-2xl bg-wash-sunshine p-4 text-sm font-medium leading-6 text-ink-soft">
          {mode === 'colorimetry'
            ? 'Beer–Lambert law: A = εcl. At a fixed wavelength and path length, absorbance is proportional to concentration within the calibrated range.'
            : AAS_ELEMENT_SPECIFIC_EXPLANATION}
        </div>
      </aside>

      <article className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          {mode === 'colorimetry' ? 'Colorimetry' : 'Atomic absorption spectroscopy'} ·{' '}
          {problem.analyte} at {problem.wavelengthNm.toFixed(1)} nm
        </p>
        <h2 className="mt-2 text-2xl font-extrabold text-ink">{problem.title}</h2>
        <p className="mt-2 text-sm font-medium leading-6 text-slate2">{problem.context}</p>
        <p className="mt-1 text-xs font-bold text-slate2">{problem.slopeNote}</p>

        <div className="mt-4 grid max-w-xs grid-cols-2 rounded-full bg-surface p-1" aria-label="Explore or practise">
          {(['explore', 'practice'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={view === option}
              onClick={() => setView(option)}
              className={`rounded-full px-3 py-2 text-sm font-extrabold ${view === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'}`}
            >
              {option === 'explore' ? 'Explore' : 'Practice'}
            </button>
          ))}
        </div>

        {view === 'explore' ? <CalibrationExplore key={problem.id} problem={problem} /> : null}

        {view === 'practice' ? (
          <>
        <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <div className="min-w-0">
            <table className="w-full text-left text-sm">
              <caption className="mb-2 text-left text-sm font-extrabold text-ink">
                Standards ({problem.unit})
              </caption>
              <thead>
                <tr className="text-xs font-extrabold uppercase tracking-[0.1em] text-slate2">
                  <th className="py-1">Concentration</th>
                  <th className="py-1">Absorbance</th>
                </tr>
              </thead>
              <tbody>
                {readings.map((point) => (
                  <tr key={point.concentration} className="border-t border-hairline">
                    <td className="py-2 font-bold text-ink">{formatConcentration(point.concentration)}</td>
                    <td className="py-2">
                      {measured.includes(point.concentration) ? (
                        <span className="font-mono font-bold text-ink">{point.absorbance.toFixed(3)}</span>
                      ) : (
                        <button
                          type="button"
                          disabled={fit !== null}
                          onClick={() => setMeasured((current) => [...current, point.concentration])}
                          aria-label={`Measure standard ${formatConcentration(point.concentration)}`}
                          className="rounded-full border border-hairline px-3 py-1 text-xs font-extrabold text-ink-soft hover:bg-surface disabled:opacity-40"
                        >
                          Measure
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                disabled={measuredPoints.length < MIN_CALIBRATION_STANDARDS || fit !== null}
                onClick={() => setFit(fitCalibration(measuredPoints))}
                className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <LineChart className="h-4 w-4" aria-hidden="true" /> Fit the line
              </button>
              <button
                type="button"
                disabled={!fit || unknownMeasured}
                onClick={() => setUnknownMeasured(true)}
                className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink-soft hover:bg-surface disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Ruler className="h-4 w-4" aria-hidden="true" /> Measure the unknown
              </button>
            </div>
            {!fit ? (
              <p className="mt-2 text-xs font-bold text-slate2">
                Measure at least {MIN_CALIBRATION_STANDARDS} standards to fit a line (
                {measuredPoints.length} measured).
              </p>
            ) : (
              <p className="mt-3 rounded-xl bg-wash-sky px-3 py-2 font-mono text-xs font-bold text-ink">
                A = {fit.slope.toFixed(4)}c {fit.intercept < 0 ? '−' : '+'}{' '}
                {Math.abs(fit.intercept).toFixed(4)} · R² = {fit.rSquared.toFixed(4)} · range{' '}
                {formatConcentration(fit.minConcentration)}–{formatConcentration(fit.maxConcentration)}
              </p>
            )}
          </div>
          <CalibrationChart problem={problem} points={measuredPoints} fit={fit} unknownA={unknownA} />
        </div>

        {fit && unknownA !== null ? (
          <section className="mt-6 rounded-2xl border border-hairline bg-surface p-4" aria-labelledby="calibration-answer-heading">
            <h3 id="calibration-answer-heading" className="text-lg font-extrabold text-ink">
              Unknown A = {unknownA.toFixed(3)}
            </h3>
            {problem.dilutionFactor > 1 ? (
              <p className="mt-1 text-sm font-bold text-ink-soft">
                Dilution factor: {problem.dilutionFactor}
              </p>
            ) : null}
            <label htmlFor="calibration-answer" className="mt-3 block text-sm font-extrabold text-ink">
              Concentration in the original sample ({problem.unit})
            </label>
            <div className="mt-2 flex flex-col gap-3 sm:flex-row">
              <input
                id="calibration-answer"
                type="number"
                inputMode="decimal"
                min={0}
                step="any"
                value={answerText}
                onChange={(event) => setAnswerText(event.target.value)}
                className="w-full max-w-[12rem] rounded-xl border border-hairline bg-white px-3 py-2 text-sm font-bold text-ink"
              />
              <button
                type="button"
                disabled={answerText.trim() === ''}
                onClick={() => check(false)}
                className="rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Check concentration
              </button>
              <button
                type="button"
                onClick={() => check(true)}
                className="rounded-full border border-hairline bg-white px-5 py-3 text-sm font-extrabold text-ink-soft hover:bg-canvas"
              >
                Outside the calibration range
              </button>
            </div>
            {feedback ? (
              <div
                role="status"
                className={`mt-4 rounded-2xl border p-4 ${
                  feedback.correct
                    ? 'border-brand-mint/45 bg-wash-mint'
                    : 'border-brand-coral/45 bg-wash-coral'
                }`}
              >
                <p className="flex items-center gap-2 font-extrabold text-ink">
                  {feedback.correct ? (
                    <CheckCircle2 className="h-5 w-5 text-brand-mint" aria-hidden="true" />
                  ) : (
                    <XCircle className="h-5 w-5 text-brand-coral" aria-hidden="true" />
                  )}
                  {feedback.correct ? 'Correct reading' : 'Check the calibration'}
                </p>
                <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">{feedback.text}</p>
              </div>
            ) : null}
          </section>
        ) : null}
          </>
        ) : null}
      </article>
    </div>
  );
}
